import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, CalendarDays, Clock, Check } from "lucide-react";
import { submitBooking } from "@/lib/api";
import { toast } from "sonner";

const TIME_SLOTS = [
  "09:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "02:00 PM", "03:00 PM",
  "04:00 PM", "05:00 PM",
];

export const BookingDialog = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(1); // 1=datetime, 2=details, 3=done
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", topic: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reset = () => {
    setStep(1); setDate(undefined); setTime(""); setForm({ name: "", email: "", company: "", phone: "", topic: "" }); setErrors({});
  };

  const handleClose = (v) => { if (!v) reset(); onOpenChange(v); };

  const goToDetails = () => {
    const err = {};
    if (!date) err.date = "Please choose a date";
    if (!time) err.time = "Please choose a time";
    setErrors(err);
    if (Object.keys(err).length) return;
    setStep(2);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.name.trim()) err.name = "Name is required";
    if (!form.email.trim()) err.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Enter a valid email";
    setErrors(err);
    if (Object.keys(err).length) return;

    setLoading(true);
    try {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const isoDate = `${yyyy}-${mm}-${dd}`;
      await submitBooking({
        name: form.name, email: form.email, company: form.company || null,
        phone: form.phone || null, topic: form.topic || null,
        preferred_date: isoDate, preferred_time: time,
      });
      toast.success("Booking request received. You'll get a confirmation shortly.");
      setStep(3);
    } catch {
      toast.error("Could not submit booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const today = new Date(); today.setHours(0,0,0,0);
  const disabledDays = (d) => {
    const day = d.getDay();
    return d < today || day === 0 || day === 6;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-3xl bg-[#0A192F] border border-[#D4AF37]/40 text-[#F8F9FA] p-0 rounded-none gap-0 max-h-[92vh] overflow-y-auto"
        data-testid="booking-dialog"
      >
        <DialogHeader className="px-8 pt-8 pb-6 border-b border-[#233554]">
          <div className="flex items-center gap-3 mb-2">
            <div className="gold-rule" />
            <span className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]">Step {step} of {step === 3 ? 3 : 2}</span>
          </div>
          <DialogTitle className="font-serif text-3xl text-[#F8F9FA]">
            {step === 1 && "Choose a date & time"}
            {step === 2 && "Your details"}
            {step === 3 && "Request received"}
          </DialogTitle>
          <DialogDescription className="text-[#A8B2D1]">
            {step === 1 && "Pick a weekday and a slot that works for you. All times are local."}
            {step === 2 && "A short note about your project helps Deepshikha prepare."}
            {step === 3 && "Deepshikha will confirm via email within one business day."}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1 */}
        {step === 1 && (
          <div className="p-8 grid md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4 text-[#D4AF37]">
                <CalendarDays size={16} />
                <span className="text-xs uppercase tracking-[0.25em]">Select Date</span>
              </div>
              <div className="border border-[#233554] p-3 bg-[#051024]" data-testid="booking-calendar">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={disabledDays}
                  className="text-[#F8F9FA]"
                />
              </div>
              {errors.date && <p className="text-xs text-red-400 mt-2">{errors.date}</p>}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-4 text-[#D4AF37]">
                <Clock size={16} />
                <span className="text-xs uppercase tracking-[0.25em]">Select Time</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {TIME_SLOTS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTime(t)}
                    className={`py-3 text-sm border transition-colors ${
                      time === t
                        ? "bg-[#D4AF37] text-[#051024] border-[#D4AF37]"
                        : "border-[#233554] text-[#F8F9FA] hover:border-[#D4AF37]"
                    }`}
                    data-testid={`time-slot-${t.replace(/[: ]/g, "")}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {errors.time && <p className="text-xs text-red-400 mt-2">{errors.time}</p>}

              <button
                type="button"
                onClick={goToDetails}
                className="mt-8 w-full bg-[#D4AF37] text-[#051024] hover:bg-[#F1D570] transition-colors px-6 py-4 text-sm font-semibold tracking-wide"
                data-testid="booking-next-btn"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <form onSubmit={onSubmit} className="p-8" data-testid="booking-form">
            <div className="bg-[#051024] border border-[#233554] p-4 mb-6 flex items-center gap-3 text-sm">
              <CalendarDays size={16} className="text-[#D4AF37]" />
              <span className="text-[#F8F9FA]">
                {date?.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })} · {time}
              </span>
              <button type="button" onClick={() => setStep(1)} className="ml-auto text-xs text-[#D4AF37] hover:underline" data-testid="booking-edit-btn">Change</button>
            </div>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label className="text-xs text-[#A8B2D1] uppercase tracking-widest mb-2 block">Full Name *</label>
                <input value={form.name} onChange={update("name")} className="bg-transparent border-b border-[#233554] focus:border-[#D4AF37] text-white py-2 outline-none w-full" data-testid="booking-input-name" />
                {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="text-xs text-[#A8B2D1] uppercase tracking-widest mb-2 block">Email *</label>
                <input type="email" value={form.email} onChange={update("email")} className="bg-transparent border-b border-[#233554] focus:border-[#D4AF37] text-white py-2 outline-none w-full" data-testid="booking-input-email" />
                {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="text-xs text-[#A8B2D1] uppercase tracking-widest mb-2 block">Company</label>
                <input value={form.company} onChange={update("company")} className="bg-transparent border-b border-[#233554] focus:border-[#D4AF37] text-white py-2 outline-none w-full" data-testid="booking-input-company" />
              </div>
              <div>
                <label className="text-xs text-[#A8B2D1] uppercase tracking-widest mb-2 block">Phone</label>
                <input value={form.phone} onChange={update("phone")} className="bg-transparent border-b border-[#233554] focus:border-[#D4AF37] text-white py-2 outline-none w-full" data-testid="booking-input-phone" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-[#A8B2D1] uppercase tracking-widest mb-2 block">What would you like to discuss?</label>
                <textarea rows={4} value={form.topic} onChange={update("topic")} className="bg-transparent border-b border-[#233554] focus:border-[#D4AF37] text-white py-2 outline-none w-full resize-none" data-testid="booking-input-topic" />
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button type="button" onClick={() => setStep(1)} className="border border-[#233554] text-[#A8B2D1] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors px-7 py-4 text-sm tracking-wide" data-testid="booking-back-btn">
                ← Back
              </button>
              <button type="submit" disabled={loading} className="bg-[#D4AF37] text-[#051024] hover:bg-[#F1D570] transition-colors px-7 py-4 text-sm font-semibold tracking-wide flex-1 inline-flex items-center justify-center gap-3 disabled:opacity-60" data-testid="booking-submit-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                {loading ? "Submitting..." : "Confirm Booking Request"}
              </button>
            </div>
          </form>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="p-12 text-center" data-testid="booking-success">
            <div className="w-16 h-16 mx-auto border border-[#D4AF37] flex items-center justify-center mb-6">
              <Check size={28} className="text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif text-2xl text-[#F8F9FA] mb-3">Thank you, {form.name.split(" ")[0]}.</h3>
            <p className="text-[#A8B2D1] max-w-md mx-auto">
              Your booking request for <span className="text-[#D4AF37]">{date?.toLocaleDateString()}</span> at <span className="text-[#D4AF37]">{time}</span> has been received. You'll receive a confirmation email shortly.
            </p>
            <button onClick={() => handleClose(false)} className="mt-8 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors px-7 py-4 text-sm font-semibold tracking-wide" data-testid="booking-close-btn">
              Close
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
